// Offline send OUTBOX persistence. A message sent while offline (or that fails
// the first network attempt) is written here alongside its optimistic
// `messages` bubble, then replayed by the drainer (features/chat/sendOutbox.ts)
// on reconnect / `online` / app boot. Keyed by the local-echo message id.

import type { OutboxItem } from './db';
import { db } from './db';

/** Write-ahead enqueue: store the row BEFORE `conv.send` so an app crash
 *  mid-send still leaves the message recoverable. Idempotent (put on id). */
export async function enqueueOutbox(item: OutboxItem): Promise<void> {
  await db.outbox.put(item);
}

/** Remove a row once its send is confirmed (or it's permanently abandoned). */
export async function dequeueOutbox(id: string): Promise<void> {
  await db.outbox.delete(id);
}

export async function getOutboxItem(id: string): Promise<OutboxItem | undefined> {
  return db.outbox.get(id);
}

export async function outboxCount(): Promise<number> {
  return db.outbox.count();
}

/** All rows whose backoff has elapsed (`nextAttemptAt <= now`), in FIFO
 *  (createdAt) order so a conversation's messages replay in the order typed. */
export async function listOutboxReady(nowMs: number): Promise<OutboxItem[]> {
  const all = await db.outbox.toArray();
  return all
    .filter((i) => i.nextAttemptAt <= nowMs)
    .sort((a, b) => a.createdAt - b.createdAt);
}

/** Record a failed attempt and push the next retry out with capped exponential
 *  backoff (1s, 2s, 4s … 30s). Returns the new attempt count so the drainer can
 *  decide when to give up. */
export async function bumpOutboxAttempt(id: string, nowMs: number): Promise<number> {
  const item = await db.outbox.get(id);
  if (item === undefined) return 0;
  const attempts = item.attempts + 1;
  const backoffMs = Math.min(30_000, 1000 * 2 ** Math.min(attempts - 1, 5));
  await db.outbox.update(id, { attempts, nextAttemptAt: nowMs + backoffMs });
  return attempts;
}
